import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { CreditByMonthTable, CREDIT_BASES, type CreditBasis } from './CreditByMonthTable';
import { CreditMonthLinesView } from './CreditMonthLines';
import { useCreditGoal, useCreditMonthLines, useSetCreditGoalOverride } from '@/hooks/queries/useGoals';
import { useUrlNumber, useUrlParam } from '@/hooks/useUrlState';

/** The hash the dashboard's credit tile links to. */
export const SECTION_ID = 'credit-by-month';

export const MONTH_WINDOWS = [6, 12, 24] as const;
export const DEFAULT_MONTH_WINDOW = 12;
/** Mirrors `MAX_MONTHS` in the api's `services/goals/credit-report.ts`. */
export const MAX_MONTH_WINDOW = 36;

/**
 * The window is this widget's own state, so it reads `?months=` itself rather than taking it as a
 * prop — the same split `SpreadAnalysis` uses for its `?tab=`. Nothing else on the analytics page
 * cares how many months this table shows.
 */
export function CreditByMonthSection() {
  const [urlMonths, setUrlMonths] = useUrlNumber('months', DEFAULT_MONTH_WINDOW, {
    min: 1, integer: true,
  });
  // The API clamps anything above its own cap, so a URL asking for more would put a 36-month
  // table under a link claiming 40. Clamped on both sides — read and write — so what the table
  // shows and what the URL says are the same number.
  const months = Math.min(urlMonths, MAX_MONTH_WINDOW);
  const setMonths = (next: number) => setUrlMonths(Math.min(next, MAX_MONTH_WINDOW));

  const { data, isLoading, isError } = useCreditGoal(months);
  const setOverride = useSetCreditGoalOverride();

  // Net is the rule everywhere else in the app; `?basis=gross` is this panel's own second view,
  // for "how much premium did I sell". It lives here and not on the dashboard tile on purpose: a
  // tile whose goal progress depends on a forgotten setting is worse than no toggle.
  const [basis, setBasis] = useUrlParam<CreditBasis>('basis', 'net', CREDIT_BASES);

  // `?month=2026-09` opens that month's line items — what the dashboard tile's total links to.
  // Only a month the table is actually showing can be open: a stale or hand-edited value reads as
  // closed, and fires no fetch.
  const [urlMonth, setUrlMonth] = useUrlParam('month', '');
  const expandedMonth = data?.months.some((m) => m.month === urlMonth) ? urlMonth : null;
  const lines = useCreditMonthLines(expandedMonth);

  // The dashboard tile links here as `/analytics#credit-by-month`. Scroll on arrival, and again
  // once the table has loaded: the section grows when its rows land, and a scroll made against
  // the spinner's height leaves the table half off-screen.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === `#${SECTION_ID}`) document.getElementById(SECTION_ID)?.scrollIntoView?.();
  }, [hash, isLoading]);

  return (
    <Card id={SECTION_ID} data-testid="credit-by-month" className="scroll-mt-20">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Credit by month</CardTitle>
          <CardDescription data-testid="credit-basis-description">
            {basis === 'net'
              ? 'Net credit each month — sold, less the cost of closing and rolling — against the goal'
              : 'Gross credit sold each month against the goal, before any costs'}
          </CardDescription>
        </div>
        <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
        <div className="flex gap-1" role="group" aria-label="Measure the goal on">
          {CREDIT_BASES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={option === basis}
              onClick={() => setBasis(option)}
              className={`px-2 py-1 text-xs rounded capitalize ${
                option === basis ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="flex gap-1" role="group" aria-label="Months shown">
          {MONTH_WINDOWS.map((window) => (
            <button
              key={window}
              type="button"
              aria-pressed={window === months}
              onClick={() => setMonths(window)}
              className={`px-2 py-1 text-xs rounded ${
                window === months ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {window}
            </button>
          ))}
        </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : isError || !data ? (
          <p className="text-sm text-gray-500">Could not load</p>
        ) : (
          <CreditByMonthTable
            months={data.months}
            basis={basis}
            onSetOverride={(month, target) => setOverride.mutate({ month, target })}
            expandedMonth={expandedMonth}
            onToggleMonth={(month) => setUrlMonth(month ?? '')}
            expandedContent={
              lines.isLoading ? (
                <div className="flex items-center justify-center py-4"><Spinner /></div>
              ) : lines.isError || !lines.data ? (
                <p className="text-sm text-gray-500">Could not load this month</p>
              ) : (
                <CreditMonthLinesView data={lines.data} />
              )
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
